import { useEffect, useState } from "react";
import { Button, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconSparkles, IconArrowRight } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { TrialModal, hasUsedTrial } from "./TrialModal";
import { InfoHint } from "../InfoHint/InfoHint";

interface TrialBannerProps {
    /** "compact" for the hero row, "card" for /account top banner */
    variant?: "compact" | "card";
}

export function TrialBanner({ variant = "card" }: TrialBannerProps) {
    const { t } = useTranslation("translation");
    const [opened, { open, close }] = useDisclosure(false);
    const [used, setUsed] = useState(hasUsedTrial());

    useEffect(() => {
        // In dev keep the banner visible across grants so the demo can be
        // re-played from a clean state — production hides it once used.
        if (process.env.NODE_ENV === "development") return;
        const handler = () => setUsed(true);
        window.addEventListener("lmutv-trial-granted", handler);
        return () => window.removeEventListener("lmutv-trial-granted", handler);
    }, []);

    if (used) return null;

    if (variant === "compact") {
        return (
            <>
                <Button
                    variant="subtle"
                    color="lmuTVColors"
                    leftSection={<IconSparkles size={16} />}
                    rightSection={<InfoHint label={t("trial.policyShort")} width={260} />}
                    onClick={open}
                >
                    {t("trial.cta.compact")}
                </Button>
                <TrialModal opened={opened} onClose={close} />
            </>
        );
    }

    return (
        <>
            <Paper
                p="md"
                radius="md"
                withBorder
                style={{
                    background: "linear-gradient(135deg, rgba(220,61,49,0.10), rgba(153,24,13,0.04))",
                    borderColor: "rgba(153,24,13,0.4)",
                }}
            >
                <Group justify="space-between" wrap="wrap" gap="md">
                    <Group gap="md" wrap="nowrap">
                        <ThemeIcon size={42} radius="xl" variant="light" color="lmuTVColors">
                            <IconSparkles size={22} />
                        </ThemeIcon>
                        <Stack gap={2}>
                            <Group gap={6}>
                                <Text fw={700}>{t("trial.banner.title")}</Text>
                                <InfoHint label={t("trial.policyShort")} width={260} />
                            </Group>
                            <Text size="sm" c="dimmed">{t("trial.banner.body")}</Text>
                        </Stack>
                    </Group>
                    <Button
                        color="lmuTVColors"
                        rightSection={<IconArrowRight size={14} />}
                        onClick={open}
                    >
                        {t("trial.cta.activate")}
                    </Button>
                </Group>
            </Paper>
            <TrialModal opened={opened} onClose={close} />
        </>
    );
}
